/**
 * Web component: Grid teaser (grid-teaser)
 * -----------------------------------------------------------------------------
 * Grid teaser blocks with scheduled displays and copyable promo code.
 *
 */
/* eslint no-unused-vars: ['error', { 'argsIgnorePattern': 'component' }] */
import './grid-teaser.scss';

import { vueReady } from '~/core/runners';

import classes from '~/config/classes';
import timings from '~/config/timings';
import { dateTime } from '~/helpers/general';
import { checkIfActiveNonPrivate } from '~/helpers/teasers';
/**
 * DOM selectors.
 */
const selectors = {
  copyButton: '[js-grid-teaser="copyButton"]',
  // fallback currently not used for grid-teasers
  fallback: '[js-grid-teaser="fallback"]',
  teaser: '[js-grid-teaser="teaser"]',
  tooltip: '[js-grid-teaser="tooltip"]',
  promoText: '[js-grid-teaser="promoText"]',
};

/**
 * Create new <grid-teaser> custom element.
 */
class GridTeaser extends HTMLElement {
  data(component) {
    return {
      selectors: {
        teasers: [...component.querySelectorAll(selectors.teaser)],
        promoTexts: component.querySelectorAll(selectors.promoText),
        copyButtons: component.querySelectorAll(selectors.copyButton),
      },
    };
  }

  /**
   * Component lifecycle - before mount.
   */
  constructor() {
    super();
    this.$data = this.data(this);
    this.teasersUpdated = false;

    // for initial load, read campaign state from local storage and update
    // teasers if necessary
    const { copyButtons } = this.$data.selectors;
    if (copyButtons) {
      const discountCampaigns = JSON.parse(
        localStorage.getItem('discount-campaigns'),
      );
      this.updateTeasers(discountCampaigns);

      // listen to campaign changes, update teaser content
      cnvs.EventBus.listen('discount-campaign', (campaigns) => {
        this.updateTeasers(campaigns);
      });
    }
  }

  /**
   * Component lifecycle - mounted.
   */
  connectedCallback() {
    this.$data = Object.keys(this.$data).length ? this.$data : this.data(this);
    this.addEventListener('click', this.handleCopy);
    let count = 0;

    /**
     * Go through each teaser and display if valid and end loop.
     */
    for (const teaser of this.$data.selectors.teasers) {
      const result = dateTime(teaser.dataset.start, teaser.dataset.end);

      if (!result || count >= 4) {
        teaser.classList.add(classes.hidden);
        teaser.classList.remove(classes.visible);
        continue;
      }

      count++;
      teaser.classList.add(classes.visible);
      teaser.classList.remove(classes.hidden);
    }
  }

  /**
   * Update teaser content based on campaign store state (code applied or not).
   * Grid teaser usually contains multiple teasers. Update all.
   * @param {Object} campaigns Can be cnvs.discount-campaigns (localStorage) or
   * object passed in 'discount-campaign' event emitted by discount-campaigns.vue
   */
  updateTeasers(campaigns) {
    this.$data = Object.keys(this.$data).length ? this.$data : this.data(this);

    const { promoTexts, copyButtons } = this.$data.selectors;

    if (!copyButtons) {
      return;
    }

    const copyButtonIcons = this.querySelectorAll(
      `${selectors.copyButton} svg`,
    );

    if (checkIfActiveNonPrivate(campaigns)) {
      const copyButtonTexts = this.querySelectorAll(
        `${selectors.copyButton} span:first-child`,
      );

      copyButtons.forEach((copyButton) => {
        copyButton.disabled = true;
      });

      copyButtonIcons.forEach((copyButtonIcon) => {
        copyButtonIcon.style.display = 'none';
      });

      copyButtonTexts.forEach((copyButtonText) => {
        copyButtonText.innerText = campaigns.active.code[0];
      });

      promoTexts.forEach((promoText) => {
        promoText.innerText = promoText.dataset.codeApplied;
      });

      this.teasersUpdated = true;
    } else if (this.teasersUpdated) {
      // reset everything to initial state
      copyButtons.forEach((copyButton) => {
        const copyButtonText = copyButton.querySelector('span');

        copyButton.disabled = false;
        copyButtonText.innerText = copyButton.dataset.code;
      });

      copyButtonIcons.forEach((copyButtonIcon) => {
        copyButtonIcon.style.display = 'unset';
      });

      promoTexts.forEach((promoText) => {
        promoText.innerText = promoText.dataset.text;
      });

      this.teasersUpdated = false;
    }
  }

  /**
   * Copy promo code and toggle tooltip.
   * @param {Event} event - Click event.
   */
  handleCopy(event) {
    if (!event.target.matches(selectors.copyButton)) {
      return;
    }

    const copyButton = event.target;
    const tooltip = event.target.querySelector(selectors.tooltip);

    if (!copyButton) {
      return;
    }

    const code = copyButton.dataset.code;
    navigator.clipboard.writeText(code);

    if (!tooltip) {
      return;
    }

    tooltip.classList.add(classes.active);

    setTimeout(() => {
      tooltip.classList.remove(classes.active);
    }, timings.message);
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('grid-teaser')) {
    return;
  }

  customElements.define('grid-teaser', GridTeaser);
});
